import { useEffect, useState } from 'react';
import { getAllowedEmailDomain } from '../lib/auth-domain';
import { getAuthErrorMessage } from '../lib/auth-errors';
import { createClient, isSupabaseConfigured } from '../lib/supabase';

function parseAuthErrorFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, ''),
  );
  return (
    params.get('error') ??
    hashParams.get('error_description') ??
    hashParams.get('error')
  );
}

function clearAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  url.hash = '';
  window.history.replaceState({}, '', url.pathname + url.search);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const domain = getAllowedEmailDomain();

  useEffect(() => {
    const code = parseAuthErrorFromUrl();
    if (code) {
      setError(getAuthErrorMessage(code) ?? code);
      clearAuthParamsFromUrl();
    }
  }, []);

  async function signInWithEmailPassword(e) {
    e.preventDefault();

    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
      return;
    }

    setPending(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError(
          getAuthErrorMessage(signInError.message) ?? signInError.message,
        );
        setPending(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
      setPending(false);
    }
  }

  async function signInWithMicrosoft() {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
      return;
    }

    setPending(true);
    setError(null);
    const supabase = createClient();
    const redirectTo = window.location.origin;

    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo,
          scopes: 'email',
          skipBrowserRedirect: true,
        },
      });

      if (oauthError) {
        setError(oauthError.message);
        setPending(false);
        return;
      }

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }

      setError(
        'Microsoft sign-in did not return a URL. Check Azure provider settings in Supabase.',
      );
      setPending(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-dark text-sky-300/90 flex items-center justify-center p-8 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-pulse"
            style={{
              left: `${(i * 17) % 100}%`,
              top: `${(i * 23) % 100}%`,
              animationDelay: `${(i % 5) * 0.4}s`,
              animationDuration: `${2 + (i % 4)}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-md glass-card rounded-2xl p-8 shadow-glass-glow-cyan">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="TKO Network Visualizer"
            className="h-14 w-auto mx-auto mb-4 opacity-90"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <h1 className="text-xl font-orbitron text-sky-100 tracking-wider">
            TKO Network Visualizer
          </h1>
        </div>

        <p className="text-center text-sm text-sky-300/70 mb-6">
          Sign in with your @{domain} account to access the planning system.
        </p>

        {error ? (
          <p className="text-red-400/90 text-sm text-center mb-4 px-1">{error}</p>
        ) : null}

        <form onSubmit={(e) => void signInWithEmailPassword(e)} className="space-y-4 mb-6">
          <div>
            <label htmlFor="email" className="block text-xs text-sky-300/70 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={pending}
              placeholder={`you@${domain}`}
              className="w-full px-4 py-2.5 rounded-xl glass-input text-sky-100 placeholder:text-sky-300/40 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/50 disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs text-sky-300/70 mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={pending}
              className="w-full px-4 py-2.5 rounded-xl glass-input text-sky-100 placeholder:text-sky-300/40 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/50 disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={pending}
            className="w-full px-6 py-3 glass-button rounded-xl text-sky-100 font-orbitron tracking-wider text-sm hover:glass-button-active disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {pending ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div className="glass-divider mb-6" />

        <button
          type="button"
          onClick={() => void signInWithMicrosoft()}
          disabled
          title="Microsoft login is temporarily unavailable"
          className="w-full flex items-center justify-center gap-3 px-6 py-3 glass-button rounded-xl text-sky-300/50 font-orbitron tracking-wider text-sm opacity-50 cursor-not-allowed transition-all"
        >
          <svg
            className="w-5 h-5 shrink-0"
            viewBox="0 0 23 23"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0 0h11v11H0V0zm12 0h11v11H12V0zM0 12h11v11H0V12zm12 0h11v11H12V12z" />
          </svg>
          <span>Microsoft login temporarily unavailable</span>
        </button>
      </div>
    </div>
  );
}
